import { z } from "zod";

export interface FieldMapping {
  version: 1;
  rules: MappingRule[];
  passthroughUnmapped: boolean;
}
export type MappingRule =
  | { kind: "field"; from: string; to: string; enabled?: boolean; locked?: boolean }
  | { kind: "constant"; to: string; value: unknown }
  | { kind: "expression"; to: string; from: string[]; expr: unknown };

/** Runtime v1 gate for a persisted/posted mapping. Strict: only the `field`
 *  rule kind is accepted (constant/expression are reserved in the type for
 *  forward-compat but not yet supported, so a body carrying them fails here and
 *  is rejected at the boundary rather than throwing later in `applyMapping`). */
export const FieldMappingSchema = z.object({
  version: z.literal(1),
  rules: z.array(
    z.object({
      kind: z.literal("field"),
      from: z.string(),
      to: z.string(),
      enabled: z.boolean().optional(),
      locked: z.boolean().optional(),
    }),
  ),
  passthroughUnmapped: z.boolean(),
});

const isMarker = (k: string): boolean => k.startsWith("__");
const hasOwn = (o: Record<string, unknown>, k: string): boolean =>
  Object.prototype.hasOwnProperty.call(o, k);

/** Apply field rules to a flat properties object (v1: kind:"field" only).
 *  `__`-prefixed markers always pass through. Non-"field" kinds throw.
 *
 *  Precedence is deterministic and independent of input key order: an explicit
 *  rule's target always wins over a passthrough key of the same name. (For
 *  `{ a, z }` with `a → z`, the delivered `z` is always the remapped `a`, never
 *  whichever key happened to enumerate last.) */
export function applyMapping(
  properties: Record<string, unknown>,
  mapping: FieldMapping,
): { properties: Record<string, unknown>; dropped: string[] } {
  const rules = mapping.rules.filter((r) => (r as { enabled?: boolean }).enabled !== false);
  for (const r of rules) {
    if (r.kind !== "field") throw new Error(`mapping: ${r.kind} not yet supported`);
  }
  const fieldRules = rules as Array<Extract<MappingRule, { kind: "field" }>>;
  const froms = new Set(fieldRules.map((r) => r.from));
  const targets = new Set(fieldRules.filter((r) => r.to !== "").map((r) => r.to));

  const out: Record<string, unknown> = {};
  const dropped: string[] = [];

  // Pass 1: markers + passthrough of unmapped keys. Skip keys handled by a rule
  // (a `from`) and keys that are a rule target (a rule will write them in pass 2,
  // so a rule always wins over passthrough).
  for (const [key, val] of Object.entries(properties)) {
    if (isMarker(key)) { out[key] = val; continue; }
    if (froms.has(key)) continue;
    if (mapping.passthroughUnmapped && !targets.has(key)) out[key] = val;
  }

  // Pass 2: apply the rules (targets overwrite; deterministic by rule order).
  for (const r of fieldRules) {
    // Never read from or write to a structural "__" marker (validateMapping
    // rejects these at save; this guards a persisted-invalid config).
    if (isMarker(r.from)) continue;
    if (!hasOwn(properties, r.from)) continue; // unknown `from` → no-op
    if (r.to === "") {
      if (r.locked) throw new Error(`mapping: cannot drop locked key "${r.from}"`);
      dropped.push(r.from);
      continue;
    }
    // Never let a rule overwrite a structural "__" marker (validateMapping
    // rejects these at save; this guards a persisted-invalid config).
    if (isMarker(r.to)) continue;
    out[r.to] = properties[r.from];
  }
  return { properties: out, dropped };
}

/** Validate a mapping against a locked-key set. Empty result = valid. */
export function validateMapping(
  mapping: FieldMapping,
  opts: { lockedKeys: string[] },
): Array<{ path: string; message: string }> {
  const issues: Array<{ path: string; message: string }> = [];
  const locked = new Set(opts.lockedKeys);
  const seenTo = new Map<string, number>();
  const preservedTo = new Set<string>();
  mapping.rules.forEach((r, i) => {
    if (r.kind !== "field") { issues.push({ path: `rules.${i}`, message: `${r.kind} rules are not yet supported` }); return; }
    if (r.enabled === false) return;
    // Reserved structural markers ("__"-prefixed) must never be a rule source or
    // target — a rule writing to/from one would clobber deliver's upsert/assoc markers.
    if (isMarker(r.from) || isMarker(r.to)) {
      issues.push({ path: `rules.${i}`, message: `cannot map ${isMarker(r.from) ? "from" : "to"} a reserved "__"-prefixed key` });
      return;
    }
    // A rule flagged locked must not itself drop the key it protects.
    if (r.locked === true && r.to === "") {
      issues.push({ path: `rules.${i}`, message: `a locked rule cannot drop "${r.from}"` });
    }
    if (locked.has(r.from) && r.to !== r.from) {
      issues.push({ path: `rules.${i}`, message: `"${r.from}" is a required key and cannot be ${r.to === "" ? "dropped" : "renamed"}` });
    }
    // Writing a different source onto a locked destination overwrites the real
    // (passthrough) locked value — reject any non-self mapping targeting a locked key.
    if (locked.has(r.to) && r.from !== r.to) {
      issues.push({ path: `rules.${i}`, message: `"${r.to}" is a required key and cannot be overwritten from "${r.from}"` });
    }
    if (r.to !== "") { seenTo.set(r.to, (seenTo.get(r.to) ?? 0) + 1); preservedTo.add(r.to); }
  });
  for (const [to, n] of seenTo) if (n > 1) issues.push({ path: "rules", message: `multiple rules map to "${to}"` });
  // When passthrough is off, an unmapped locked key is silently dropped by
  // applyMapping — require each locked key to be produced by some enabled rule.
  if (!mapping.passthroughUnmapped) {
    for (const key of locked) {
      if (!preservedTo.has(key)) {
        issues.push({ path: "passthroughUnmapped", message: `"${key}" is a required key and must be mapped when passthrough is disabled` });
      }
    }
  }
  return issues;
}
